import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';

import axios from 'axios';
import { toError, assertOk } from '@/services/normalize';

import {
  BASE_URL,
  LOGIN,
  M_POST,
  FIND_SUBPASS,
  CREATE_SUBPASS,
  VER_SUBPASS,
  REFRESH_TOKEN,
  VERIFY_TICKET,
  M_GET,
  TWO_FA_SETUP_ENROLL,
  TWO_FA_ENABLE_ENROLL,
  TWO_FA_VERIFY,
  TWO_FA_STATUS,
  AMBSLOT_SECRET,
  PUBLIC_KEY_HEADER
} from '@/utils/api';

import serviceUtils from '@/services/utils';
import NodeRSA from 'node-rsa';

const RSA = new NodeRSA(PUBLIC_KEY_HEADER);

const SESSION_ID_KEY = 'session-id';
const AUTHENTICATE_KEY = 'ambgames-user-authenticate';
const BALANCE_KEY = 'ambgames-user-balance';
const REFRESH_TOKEN_KEY = 'ambgames-user-refresh-token';
const ACCESS_TOKEN_KEY = 'ambgames-user-access-token';

type AuthUser = Record<string, any>;

type AuthPayload = {
  token: string;
  tokenRefresh: string;
  user: AuthUser;
};

function ensureSessionStarted() {
  const existing = serviceUtils._session.get<string>(SESSION_ID_KEY);
  if (existing) return existing;

  const sid = `sess:${Date.now()}`;
  serviceUtils._session.set(SESSION_ID_KEY, sid);
  return sid;
}

function getBalanceFromUser(user: AuthUser): number {
  return Number(user?.wallet?.balance ?? 0);
}

function setAuthorizeHeader(token: string) {
  (axios as any).defaults.headers.common.Authorization = `Bearer ${token}`;
}

function clearAuthorizeHeader() {
  delete (axios as any).defaults.headers.common.Authorization;
}

function setXAuthorize() {
  const encryptAuthorize = RSA.encrypt(AMBSLOT_SECRET, 'base64');
  localStorage.setItem('x-authorize', encryptAuthorize);
}

function persistAuthSession(payload: AuthPayload) {
  const { token, tokenRefresh, user } = payload;
  const balance = getBalanceFromUser(user);

  ensureSessionStarted();

  const authData = {
    token,
    tokenRefresh,
    user
  };

  serviceUtils._session.set(AUTHENTICATE_KEY, JSON.stringify(authData));
  serviceUtils._session.set(BALANCE_KEY, balance);
  serviceUtils._session.set(REFRESH_TOKEN_KEY, tokenRefresh);
  serviceUtils._session.set(ACCESS_TOKEN_KEY, token);

  setAuthorizeHeader(token);

  return {
    token,
    tokenRefresh,
    balance,
    user
  };
}

function clearAuthSession() {
  serviceUtils._session.remove(SESSION_ID_KEY);
  serviceUtils._session.remove(AUTHENTICATE_KEY);
  serviceUtils._session.remove(BALANCE_KEY);
  serviceUtils._session.remove(REFRESH_TOKEN_KEY);
  serviceUtils._session.remove(ACCESS_TOKEN_KEY);
}

@Module({ name: 'auth' })
export default class Auth extends VuexModule {
  token: string = serviceUtils.getUserPersistent().token || '';

  tokenRefresh: string = (serviceUtils.getUserPersistent() as any).tokenRefresh || '';

  balance: number = serviceUtils.getUserBalance() || 0;

  user = serviceUtils.getUserPersistent().user || {};

  @Mutation
  mutateLogin(data: any) {
    const { token, tokenRefresh, balance, user } = data;
    this.token = token;
    this.tokenRefresh = tokenRefresh;
    this.balance = balance;
    this.user = user;
  }

  @Mutation
  private RESET_AUTH() {
    this.token = '';
    this.tokenRefresh = '';
    this.balance = 0;
    this.user = {};
  }

  @Action({ rawError: true })
  async Login(userdata: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOGIN,
        data: userdata,
        method: M_POST
      });

      assertOk(data, 'Login failed');

      // 2FA (ADMIN / SUPERADMIN): backend gates login with a temp token and
      // does NOT return a JWT yet. Hand the flags back to the view so it can
      // branch to the setup / verify screen. Do not persist anything here.
      if (data?.data?.requires2FASetup || data?.data?.requires2FA) {
        return data;
      }

      setXAuthorize();

      const role = data?.data?.user?.role;
      const username = data?.data?.user?.username;
      const allowed = ['SUPERADMIN', 'COMPANY'].includes(role) || username === 'aaa';
      const isByPassSecureLogin = data?.data?.user?.isByPassSecureLogin === true && Boolean(data?.data?.token);

      if (!allowed && !isByPassSecureLogin) {
        return data?.status?.code ?? 0;
      }

      const model = persistAuthSession({
        token: data.data.token,
        tokenRefresh: data.data.tokenRefresh,
        user: data.data.user
      });

      this.context.commit('mutateLogin', model);

      return data;
    } catch (err: any) {
      throw toError(err, 'Login failed');
    }
  }

  @Action({ rawError: true })
  async RefreshToken() {
    try {
      const currentRefreshToken = serviceUtils._session.get<string>(REFRESH_TOKEN_KEY);

      if (!currentRefreshToken) {
        throw toError({ status: { code: 401, message: 'No refresh token' } }, 'Refresh token failed');
      }

      const { data } = await axios({
        url: BASE_URL + REFRESH_TOKEN,
        method: M_POST,
        headers: {
          Authorization: `Bearer ${currentRefreshToken}`
        }
      });

      assertOk(data, 'Refresh token failed');

      const payload = data?.data;
      if (!payload?.token) {
        return data;
      }

      const currentUser = this.user || {};
      const currentTokenRefresh = this.tokenRefresh || currentRefreshToken;

      const model = persistAuthSession({
        token: payload.token,
        tokenRefresh: payload.tokenRefresh || currentTokenRefresh,
        user: payload.user || currentUser
      });

      this.context.commit('mutateLogin', model);

      return model;
    } catch (err: any) {
      throw toError(err, 'Refresh token failed');
    }
  }

  @Action({ rawError: true })
  async check2Pass(username: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + FIND_SUBPASS,
        data: username,
        method: M_POST
      });

      assertOk(data, 'Check 2-pass failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Check 2-pass failed');
    }
  }

  @Action({ rawError: true })
  async create2Pass(subPassFirst: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + CREATE_SUBPASS,
        data: subPassFirst,
        method: M_POST
      });

      assertOk(data, 'Create 2-pass failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Create 2-pass failed');
    }
  }

  @Action({ rawError: true })
  async verify2Pass(confirmSubpass: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + VER_SUBPASS,
        data: confirmSubpass,
        method: M_POST
      });

      assertOk(data, 'Verify 2-pass failed');

      setXAuthorize();

      const model = persistAuthSession({
        token: data.data.token,
        tokenRefresh: data.data.tokenRefresh,
        user: data.data.user
      });

      this.context.commit('mutateLogin', model);

      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Verify 2-pass failed');
    }
  }

  // ===== Google 2FA (ADMIN / SUPERADMIN) =====

  // Step 2 of first-time setup: exchange tempSetupToken + email for the
  // otpauth URL + secret used to build the QR on the frontend.
  @Action({ rawError: true })
  async setupEnroll(payload: { tempSetupToken: string; email: string }) {
    try {
      const { data } = await axios({
        url: BASE_URL + TWO_FA_SETUP_ENROLL,
        data: payload,
        method: M_POST
      });

      assertOk(data, 'Setup 2FA failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Setup 2FA failed');
    }
  }

  // Step 3 of first-time setup: confirm the OTP and receive the JWT.
  @Action({ rawError: true })
  async enableEnroll(payload: { tempSetupToken: string; otp: string }) {
    try {
      const { data } = await axios({
        url: BASE_URL + TWO_FA_ENABLE_ENROLL,
        data: payload,
        method: M_POST
      });

      assertOk(data, 'Enable 2FA failed');

      setXAuthorize();

      const model = persistAuthSession({
        token: data.data.token,
        tokenRefresh: data.data.tokenRefresh,
        user: data.data.user
      });

      this.context.commit('mutateLogin', model);

      return data;
    } catch (err: any) {
      throw toError(err, 'Enable 2FA failed');
    }
  }

  // Subsequent logins: verify the OTP against tempToken and receive the JWT.
  @Action({ rawError: true })
  async verify2FA(payload: { tempToken: string; otp: string }) {
    try {
      const { data } = await axios({
        url: BASE_URL + TWO_FA_VERIFY,
        data: payload,
        method: M_POST
      });

      assertOk(data, 'Verify 2FA failed');

      setXAuthorize();

      const model = persistAuthSession({
        token: data.data.token,
        tokenRefresh: data.data.tokenRefresh,
        user: data.data.user
      });

      this.context.commit('mutateLogin', model);

      return data;
    } catch (err: any) {
      throw toError(err, 'Verify 2FA failed');
    }
  }

  // Optional: read 2FA status once authenticated (Authorization is attached
  // automatically by the axios interceptor).
  @Action({ rawError: true })
  async get2FAStatus() {
    try {
      const { data } = await axios({
        url: BASE_URL + TWO_FA_STATUS,
        method: M_GET
      });

      assertOk(data, 'Get 2FA status failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Get 2FA status failed');
    }
  }

  @Action({ rawError: true })
  async verifyTicketLogin(ticket: string) {
    try {
      const { data } = await axios({
        url: `${BASE_URL + VERIFY_TICKET}?ticket=${encodeURIComponent(ticket)}`,
        method: M_GET
      });

      assertOk(data, 'Verify ticket login failed');

      const payload = data?.data;
      const token = payload?.token;
      const tokenRefresh = payload?.tokenRefresh;
      const secureCode = payload?.secureCode;
      const user = payload?.user;

      if (secureCode) {
        localStorage.setItem('x-secure-code', secureCode);
      }

      setXAuthorize();

      const model = persistAuthSession({
        token,
        tokenRefresh,
        user
      });

      this.context.commit('mutateLogin', model);

      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Verify ticket login failed');
    }
  }

  @Action({ rawError: true })
  async Logout(): Promise<void> {
    localStorage.removeItem('x-secure-code');
    localStorage.removeItem('x-authorize');

    serviceUtils.clearUserPersistent();
    clearAuthSession();
    clearAuthorizeHeader();

    this.context.commit('RESET_AUTH');
  }

  @Mutation
  setProfile(data: any) {
    this.user = data;
    this.balance = this.user?.wallet?.balance ?? 0;
  }

  get isAuthenticated() {
    return this.token;
  }

  get userPermission() {
    return this.user && this.user.userPermission;
  }

  get role() {
    return this.user && this.user.role;
  }

  get isWhiteLabel() {
    return this.user && this.user.isWhiteLabel;
  }

  get userBalance() {
    return serviceUtils.currencyFormatter(this.user?.wallet?.balance ?? 0) || 0;
  }
}
